import type { HTMLAttributes, UIEventHandler } from "react";
import React, { useImperativeHandle, useMemo, useRef, useState } from "react";

import cn from "classnames";
// Плагин eslint-plugin-import не умеет правильно определять экспорты из package.json
// eslint-disable-next-line import/no-unresolved
import throttle from "lodash/throttle";

import { useIsomorphicLayoutEffect } from "../../../hooks";
import { Box, boxForwardRef } from "../Box/Box";

import type { BoxComponentWithDefault } from "../Box/Box";

import classes from "./ScrollWithFade.styl";

const THROTTLE_DELAY = 100;

export interface ScrollWithFadeProps
    extends Pick<HTMLAttributes<HTMLElement>, "onScroll"> {}

/**
 * Полиморфный компонент, который добавляет скролящемуся элементу эффект fade'а
 */
export const ScrollWithFade = boxForwardRef<
    BoxComponentWithDefault<"div", ScrollWithFadeProps>
>(({ as = "div", onScroll, className, children, ...props }, ref) => {
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);

    const elementRef = useRef<HTMLElement>(null);

    const checkScroll = useMemo(
        () =>
            throttle(() => {
                const container = elementRef.current;
                if (!container) {
                    return;
                }

                const { scrollLeft, scrollWidth, clientWidth } = container;

                const isScrolledLeft = scrollLeft > 0;
                const isScrolledRight =
                    Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1;

                setShowLeftFade(isScrolledLeft);
                setShowRightFade(isScrolledRight);
            }, THROTTLE_DELAY),
        [],
    );

    const scrollHandler: UIEventHandler<HTMLElement> = (e) => {
        onScroll?.(e);
        checkScroll();
    };

    useIsomorphicLayoutEffect(() => {
        checkScroll();

        window.addEventListener("resize", checkScroll);

        return () => {
            window.removeEventListener("resize", checkScroll);
        };
    }, [checkScroll]);

    useImperativeHandle(ref, () => elementRef.current!);

    const wrapperClasses = cn(
        classes["container"],
        {
            [classes["show-left-fade"]]: showLeftFade,
            [classes["show-right-fade"]]: showRightFade,
        },
        className,
    );

    return (
        <Box
            as={as}
            {...props}
            className={wrapperClasses}
            onScroll={scrollHandler}
            ref={elementRef}
        >
            {children}
        </Box>
    );
});
